import { Directive, HostListener, forwardRef, ElementRef, Input, OnChanges } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';

// -------------------- Utils --------------------
class InputUtils {
  static readonly teclasEspeciais = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];

  static verificarTecla(event: KeyboardEvent, regex: RegExp) {
    if (InputUtils.teclasEspeciais.includes(event.key)) return;
    const atual = (event.target as HTMLInputElement).value;
    const proximo = atual + event.key;
    if (!regex.test(proximo)) event.preventDefault();
  }

  static verificarColagem(event: ClipboardEvent, regex: RegExp) {
    const textoColado = event.clipboardData?.getData('text') || '';
    if (!regex.test(textoColado)) event.preventDefault();
  }

  static ehRepetido(value: string): boolean {
    return new Set(value).size == 1;
  }

  static parseNumero(str: string): number {
    return Number.parseFloat(str);
  }
}

// -------------------- Somente Letras --------------------
@Directive({ selector: '[somenteLetras]' })
export class SomenteLetrasDirective {
  private readonly regex = /^[a-zA-ZÀ-ú\s]*$/;

  @HostListener('keydown', ['$event'])
  aoPressionarTecla(event: KeyboardEvent) { InputUtils.verificarTecla(event, this.regex); }

  @HostListener('paste', ['$event'])
  aoColar(event: ClipboardEvent) { InputUtils.verificarColagem(event, this.regex); }
}

// -------------------- Somente Números --------------------
@Directive({ selector: '[somenteNumeros]' })
export class SomenteNumerosDirective {

  @HostListener('keydown', ['$event'])
  aoPressionarTecla(event: KeyboardEvent) {
    const teclasEspeciais = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    if (teclasEspeciais.includes(event.key)) return;

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  aoColar(event: ClipboardEvent) {
    event.preventDefault();
    const texto = event.clipboardData?.getData('text') || '';
    const numeros = texto.replace(/\D/g, '');
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const novoValor = input.value.substring(0, start) + numeros + input.value.substring(end);

    input.value = novoValor;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

// -------------------- Somente Alfanumérico --------------------
@Directive({ selector: '[somenteAlfanumerico]' })
export class SomenteAlfanumericoDirective {
  private readonly regex = /^[a-zA-Z0-9À-ú\s]*$/;

  @HostListener('keydown', ['$event'])
  aoPressionarTecla(event: KeyboardEvent) { InputUtils.verificarTecla(event, this.regex); }

  @HostListener('paste', ['$event'])
  aoColar(event: ClipboardEvent) { InputUtils.verificarColagem(event, this.regex); }
}

// -------------------- Somente Data --------------------
@Directive({ selector: '[somenteData]' })
export class SomenteDataDirective {
  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replaceAll(/[^0-9/]/g, '').slice(0, 10);
  }
}

// -------------------- Formatar Data --------------------
@Directive({ selector: '[formatarData]' })
export class FormatarDataDirective {
  constructor(private readonly el: ElementRef<HTMLInputElement>) {}

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    let valor = this.el.nativeElement.value.replaceAll(/\D/g, '');
    if (valor.length > 2) valor = valor.slice(0, 2) + '/' + valor.slice(2);
    if (valor.length > 5) valor = valor.slice(0, 5) + '/' + valor.slice(5);
    if (valor.length > 10) valor = valor.slice(0, 10);

    const partes = valor.split('/');
    if (partes.length == 3) {
      const [dia, mes, ano] = partes;
      if (
        InputUtils.ehRepetido(dia + mes + ano) ||
        !this.DataValida(dia, mes, ano)
      ) {
        valor = '';
      }
    }

    this.el.nativeElement.value = valor;
    const setter = Object.getOwnPropertyDescriptor(
      HTMLInputElement.prototype,
      'value'
    )?.set;
    setter?.call(this.el.nativeElement, valor);
    this.el.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private DataValida(dia: string, mes: string, ano: string): boolean {
    if (dia.length != 2 || mes.length != 2 || ano.length != 4) return false;
    const d = Number.parseInt(dia, 10);
    const m = Number.parseInt(mes, 10);
    const a = Number.parseInt(ano, 10);
    if (Number.isNaN(d) || Number.isNaN(m) || Number.isNaN(a)) return false;
    if (d < 1 || m < 1 || m > 12) return false;
    const diasNoMes = new Date(a, m, 0).getDate();
    return d <= diasNoMes;
  }
}

// -------------------- CNPJ/CPF Formatação --------------------
@Directive({ selector: '[cnpjCpfTexto]' })
export class CnpjCpfTextoDirective implements OnChanges {
  @Input('cnpjCpfTexto') valor?: string | null;
  constructor(private readonly el: ElementRef<HTMLElement>) { }

  ngOnChanges(): void {
    const raw = (this.valor || '').replaceAll(/\D/g, '');
    let out = '-';
    if (raw.length == 11) out = raw.replaceAll(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    else if (raw.length == 14) out = raw.replaceAll(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    this.el.nativeElement.textContent = out;
  }
}

// -------------------- Placa --------------------
@Directive({ selector: '[placaTexto]' })
export class PlacaTextoDirective implements OnChanges {
  @Input('placaTexto') valor?: string | null;
  constructor(private readonly el: ElementRef<HTMLElement>) { }

  ngOnChanges(): void {
    const raw = (this.valor || '').toUpperCase().trim();
    const out = raw.replaceAll(/^([A-Z]{3})(\d{4})$/, '$1-$2') || '—';
    this.el.nativeElement.textContent = out;
  }
}

// -------------------- CPF --------------------
@Directive({
  selector: '[cpfValido]',
  standalone: true,
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => CpfValidoDirective), multi: true }]
})
export class CpfValidoDirective implements Validator {
  validate(control: AbstractControl): ValidationErrors | null {
    const cpf = (control.value || '').replaceAll(/\D/g, '');
    if (!cpf) return null;
    if (cpf.length !== 11) return { cpfInvalido: true };
    if (InputUtils.ehRepetido(cpf)) return { cpfInvalido: true };

    const calcDigito = (cpf: string, multiplicadores: number[]): number => {
      let soma = 0;
      for (let i = 0; i < multiplicadores.length; i++) {
        soma += Number.parseInt(cpf.charAt(i), 10) * multiplicadores[i];
      }
      const resto = (soma * 10) % 11;
      return resto == 10 ? 0 : resto;
    };

    const digito1 = calcDigito(cpf, [10, 9, 8, 7, 6, 5, 4, 3, 2]);
    if (digito1 !== Number.parseInt(cpf.charAt(9), 10)) return { cpfInvalido: true };
    const digito2 = calcDigito(cpf, [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
    if (digito2 !== Number.parseInt(cpf.charAt(10), 10)) return { cpfInvalido: true };

    return null;
  }
}

// -------------------- CNPJ --------------------
@Directive({
  selector: '[cnpjValido]',
  standalone: true,
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => CnpjValidoDirective), multi: true }]
})
export class CnpjValidoDirective implements Validator {
  validate(control: AbstractControl): ValidationErrors | null {
    const cnpj = (control.value || '').replaceAll(/\D/g, '');
    if (!cnpj || cnpj.length !== 14) return { cnpjInvalido: true };
    if (InputUtils.ehRepetido(cnpj)) return { cnpjInvalido: true };

    const calcDigito = (cnpj: string, pesos: number[]): number => {
      let soma = 0;
      let i = 0;
      for (const p of pesos) {
        soma += Number.parseInt(cnpj.charAt(i), 10) * p;
        i++;
      }
      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };

    const digito1 = calcDigito(cnpj, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
    const digito2 = calcDigito(cnpj.slice(0, 12) + digito1, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

    if (Number.parseInt(cnpj.charAt(12), 10) !== digito1 || Number.parseInt(cnpj.charAt(13), 10) !== digito2) {
      return { cnpjInvalido: true };
    }
    return null;
  }
}

// -------------------- RG --------------------
@Directive({
  selector: '[rgValido]',
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => RgValidoDirective), multi: true }]
})
export class RgValidoDirective implements Validator {
  validate(control: AbstractControl): ValidationErrors | null {
    const rg = (control.value || '').replaceAll(/\D/g, '');
    if (!rg || rg.length < 7 || rg.length > 9 || InputUtils.ehRepetido(rg)) return { rgInvalido: true };
    return null;
  }
}

// -------------------- Celular --------------------
@Directive({
  selector: '[celularValido]',
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => CelularValidoDirective), multi: true }]
})
export class CelularValidoDirective implements Validator {
  validate(control: AbstractControl): ValidationErrors | null {
    const celular = (control.value || '').replaceAll(/\D/g, '');
    if (!celular || celular.length !== 11 || InputUtils.ehRepetido(celular)) return { celularInvalido: true };
    return null;
  }
}

// -------------------- Email --------------------
@Directive({
  selector: '[emailValido]',
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => EmailValidoDirective), multi: true }]
})
export class EmailValidoDirective implements Validator {
  validate(control: AbstractControl): ValidationErrors | null {
    const email = (control.value || '').toLowerCase();
    if (!email) return null;
    const regex = /^[^\s@]+@[^\s@]+\.(com|br)$/;
    return regex.test(email) ? null : { emailInvalido: true };
  }
}

// -------------------- Email GOV --------------------
@Directive({
  selector: '[emailGovValido]',
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => EmailGovValidoDirective), multi: true }]
})
export class EmailGovValidoDirective implements Validator {
  validate(control: AbstractControl): ValidationErrors | null {
    const email = (control.value || '').trim();
    if (!email) return null;
    const regex = /^[A-Za-z0-9._%+-]+@([A-Za-z0-9-]+\.)+gov(\.[A-Za-z]{2,})+$/i;
    return regex.test(email) ? null : { emailInvalido: true };
  }
}
